/**
 * RAIZ 8 (2026-07-25) — reidratação do envelope de apresentação persistido.
 *
 * O defeito (M10, medido ao vivo): recarregar a página apagava o card do plano,
 * os botões e os chips. O texto continuava dizendo "os detalhes estão no card
 * abaixo" e o card não estava lá. No onboarding a pergunta voltava SEM as opções
 * para responder, e o usuário travava. Sem chips ele responde por texto, e aí cai
 * no laço da pausa (o M10 agravando o G5).
 *
 * O motor passou a gravar card, botões e chips em `Message.metadata` e a
 * devolvê-los no histórico (PR par no engine). Este módulo é o outro lado: sem
 * ele o campo chega e é descartado, porque `ApiMessage` só tinha
 * `id/content/role/createdAt`.
 *
 * O QUE ESTE MÓDULO NÃO FAZ, e é decisão do produto: nada de reconstruir o card
 * por heurística a partir do texto. Adivinhar o que o card era é o produto
 * voltando a mentir. O que o motor não persistiu não é inventado na volta.
 *
 * PURO: sem store, sem rede, sem React. Testável sozinho.
 *
 * 2026-09-10 (STO-54) — uma ressalva ao "puro" acima, para o comentário não
 * mentir: os cards de ação passam por `sanitizeActionCards`, que REGISTRA no
 * Sentry o que recusa. Continua sem store, sem rede e sem React; deixou de ser
 * livre de efeito, e de propósito — o silêncio era o defeito.
 */

import { persistedClickLabel } from './chat/clickedActionPresentation'
import { ownsTurnActions } from './chat/actionOwnership'
import { sanitizeActionCards, type ActionCard } from './parse-action-cards'

/** Campos de apresentação no shape que a store do chat usa. */
export interface PresentationFields {
  displayText?: string
  richContent?: unknown
  suggestedActions?: unknown
  webSearchMeta?: unknown
  metadata?: { actionCards?: ActionCard[] }
}

/** A forma mínima que a reidratação precisa de cada mensagem do histórico. */
export interface HistoryMessageLike {
  id: string
  role: 'user' | 'assistant'
  metadata?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

interface ToolTraceEntry {
  name: string
  params?: Record<string, unknown>
  summary?: string
  state?: 'output-available' | 'output-error'
}

function isToolTraceEntry(value: unknown): value is ToolTraceEntry {
  return isRecord(value) && typeof value['name'] === 'string'
}

/**
 * STO-103 (E1) — MESMO recuo do caminho vivo (`connectionStore.ts:1509-1520`):
 * prefere `toolTrace` (nome + parâmetro redigido + estado, motor >= 0.71.0) e
 * recua para `toolsUsed` (só nomes) quando o motor é mais velho. `status`
 * fixo em `'success'` só existe para bater o MESMO shape que o caminho vivo
 * monta — nenhum leitor confere esse campo hoje, quem pinta erro é `state`.
 */
function toolsUsedFromPersisted(
  persisted: Record<string, unknown>,
):
  | Array<{ name: string; status: 'success'; params?: unknown; summary?: string; state?: string }>
  | undefined {
  const toolTrace = persisted['toolTrace']
  if (Array.isArray(toolTrace) && toolTrace.length > 0 && toolTrace.every(isToolTraceEntry)) {
    return toolTrace.map((p) => ({
      name: p.name,
      status: 'success' as const,
      ...(p.params !== undefined ? { params: p.params } : {}),
      ...(p.summary !== undefined ? { summary: p.summary } : {}),
      ...(p.state !== undefined ? { state: p.state } : {}),
    }))
  }
  const toolsUsed = persisted['toolsUsed']
  if (
    Array.isArray(toolsUsed) &&
    toolsUsed.length > 0 &&
    toolsUsed.every((n) => typeof n === 'string')
  ) {
    return toolsUsed.map((name) => ({ name, status: 'success' as const }))
  }
  return undefined
}

function isImportContinueAction(action: unknown): action is { actionId: string; label: string } {
  return (
    isRecord(action) &&
    action['actionId'] === 'team_entry.seguir' &&
    typeof action['label'] === 'string' &&
    Boolean(action['label'].trim())
  )
}

/** Compatibility for successful team-import notices persisted without the canonical action strip. */
function legacyImportContinuation(persisted: Record<string, unknown>) {
  const notice = persisted['importTerminalNotice']
  const rich = persisted['richContent']
  if (
    !isRecord(notice) ||
    typeof notice['jobId'] !== 'string' ||
    !notice['jobId'].trim() ||
    typeof notice['state'] !== 'string' ||
    !['completed', 'completed_with_errors'].includes(notice['state']) ||
    !isRecord(rich)
  )
    return undefined
  const step = rich['onboardingRailStep']
  if (!isRecord(step) || step['flowId'] !== 'onboarding' || step['stepId'] !== 'team_entry')
    return undefined
  const actions = step['actions']
  if (!Array.isArray(actions) || actions.length !== 1) return undefined
  const action = actions[0]
  return isImportContinueAction(action)
    ? [{ actionId: action['actionId'], label: action['label'], value: '' }]
    : undefined
}

/**
 * Traduz o envelope persistido pelo motor para os campos da store.
 *
 * Envelope ausente, nulo ou de tipo inesperado devolve `{}` em vez de lançar: um
 * histórico antigo (gravado antes desta mudança) tem `metadata` nula em toda
 * mensagem, e isso é normal, não erro. Chave ausente continua ausente, nunca
 * preenchida com valor inventado.
 */
export function presentationFromPersistedMetadata(persisted: unknown): PresentationFields {
  if (!isRecord(persisted)) return {}
  const out: PresentationFields = {}
  if (persisted['richContent'] !== undefined) out.richContent = persisted['richContent']
  // STO-103 (E1) — `toolTrace`/`toolsUsed` são chaves IRMÃS de `richContent` no
  // envelope durável (`presentation-envelope.ts:88-91` no motor), não filhas
  // dele. O caminho vivo (`connectionStore.ts:1566-1572`) as ENFIA dentro de
  // `richContent.toolsUsed` porque é de lá que `MessageBubble.tsx` lê; sem este
  // passo o reload nunca monta essa chave e a linha de ferramentas (quando
  // ligada) divergiria do que estava na tela antes do F5. Sobrevive em TODO
  // turno do assistente, sem congelar: é registro do que rodou, não uma ação —
  // mesma classe de `actionCards`, nunca a de `suggestedActions`.
  const toolsUsed = toolsUsedFromPersisted(persisted)
  if (toolsUsed !== undefined) {
    const richContentBase = isRecord(out.richContent) ? out.richContent : {}
    out.richContent = { ...richContentBase, toolsUsed }
  }
  if (persisted['suggestedActions'] !== undefined) {
    out.suggestedActions = persisted['suggestedActions']
  } else {
    const actions = legacyImportContinuation(persisted)
    if (actions) out.suggestedActions = actions
  }
  if (persisted['webSearch'] !== undefined) out.webSearchMeta = persisted['webSearch']
  if (persisted['actionCards'] !== undefined) {
    // STO-54 — o MESMO portão da metade viva (`sanitizeActionCards`). Antes
    // desta linha o payload do banco entrava CRU e sem conferência nenhuma, e
    // era isso que fazia as duas metades divergirem: um turno com `category`
    // desconhecida sumia ao vivo e voltava com três botões na recarga. Um card
    // sem `label` chegava até `ActionCard.tsx:36` e derrubava a bolha.
    //
    // Chave ausente continua ausente: payload recusado não vira
    // `metadata: { actionCards: undefined }`, senão a bolha ganharia um
    // envelope vazio que o `MessageBubble` teria de saber ignorar.
    const cards = sanitizeActionCards(persisted['actionCards'], 'historico')
    if (cards !== null) out.metadata = { actionCards: cards }
  }
  return out
}

/**
 * Reidrata o histórico inteiro aplicando a regra de CONGELAR.
 *
 * Só o ÚLTIMO turno do assistente volta interativo. Este é o hazard que o mapa de
 * causa raiz nomeou: um histórico com cinco perguntas do onboarding não pode
 * voltar com os cinco conjuntos de botões clicáveis, senão o usuário clica num
 * passo que já passou e o produto age sobre uma decisão vencida.
 *
 * Os turnos anteriores mantêm o card e perdem os botões: o card é registro do que
 * aconteceu e continua legítimo na tela; o botão é uma ação, e ação de passo
 * vencido não pode existir. Turno de usuário só recebe o rótulo de clique verificado e persistido pelo motor.
 */
export function rehydratePresentation<T extends HistoryMessageLike>(
  messages: readonly T[],
): (T & PresentationFields)[] {
  // 2026-07-26 — a busca da última do assistente saiu daqui para
  // `./chat/actionOwnership.ts`. Não é refactor de gosto: o caminho VIVO
  // precisava da MESMA regra e tinha a sua própria (`isLast`, a última da
  // LISTA), que diverge desta assim que o usuário responde por escrito. Duas
  // regras parecidas em lugares diferentes é o padrão que produziu o M10 no
  // motor; agora existe uma só, e um teste roda as duas pontas lado a lado.
  return messages.map((message, index): T & PresentationFields => {
    if (message.role !== 'assistant') {
      const displayText =
        message.role === 'user' ? persistedClickLabel(message.metadata) : undefined
      return { ...message, ...(displayText ? { displayText } : {}) } as T & PresentationFields
    }
    const fields = presentationFromPersistedMetadata(message.metadata)
    if (!ownsTurnActions(messages, index)) {
      // Congelado: card sim, ação não.
      delete fields.suggestedActions
    }
    return { ...message, ...fields }
  })
}
